import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
  useCreateCategoryGroupMutation,
  useDeleteCategoryGroupMutation,
  useReorderCategoryGroupsMutation,
  useUpdateCategoryGroupMutation,
} from '@transactions/graphql';
import {categoryColors} from 'common/categoryAppearance';
import {
  ArrowDownAZ,
  Ban,
  Check,
  GripVertical,
  Palette,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import * as React from 'react';
import {
  addCategoryGroup,
  setCategoryGroups,
} from 'src/apolloCache/categoryGroups';
import {Button} from 'src/Button';
import {
  categorySwatchClasses,
  validCategoryColorOrNull,
} from 'src/categoryAppearance';
import {CategoryAvatar} from 'src/CategoryAvatar';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import type {DragEndEvent} from '@dnd-kit/core';
import type {
  FullCategoryFragment,
  FullCategoryGroupFragment,
} from '@transactions/graphql';

const GROUP_PREFIX = 'grp:';

type MutationKind = 'reorder' | 'create' | 'rename' | 'delete';

const MUTATION_KINDS: MutationKind[] = [
  'reorder',
  'create',
  'rename',
  'delete',
];

const MUTATION_KIND_LABELS: Record<MutationKind, string> = {
  reorder: "Couldn't save the new order",
  create: "Couldn't create the group",
  rename: "Couldn't rename the group",
  delete: "Couldn't delete the group",
};

type Draft = {
  /** Group IDs in display order. */
  order: string[];
  /** Category IDs per group, in display order. */
  members: Record<string, string[]>;
};

const buildDraft = (
  groups: FullCategoryGroupFragment[],
  previous?: Draft,
): Draft => {
  // Keep the previous draft's order where possible so an in-flight drag
  // isn't clobbered; drop removed groups/categories, append new ones.
  const groupIds = new Set(groups.map((g) => g._id));
  const keptOrder = (previous?.order ?? []).filter((id) => groupIds.has(id));
  const keptSet = new Set(keptOrder);
  const order = [
    ...keptOrder,
    ...groups.map((g) => g._id).filter((id) => !keptSet.has(id)),
  ];

  const members: Record<string, string[]> = {};
  for (const group of groups) {
    const memberSet = new Set(group.categoryIds);
    const kept = (previous?.members[group._id] ?? []).filter((id) =>
      memberSet.has(id),
    );
    const keptMembers = new Set(kept);
    members[group._id] = [
      ...kept,
      ...group.categoryIds.filter((id) => !keptMembers.has(id)),
    ];
  }
  return {order, members};
};

type RowProps = {
  category: FullCategoryFragment;
  sortable: boolean;
};

const RearrangeCategoryRow = ({category, sortable}: RowProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({id: category._id, disabled: !sortable});

  return (
    <div
      ref={setNodeRef}
      style={{transform: CSS.Transform.toString(transform), transition}}
      className={`border-border bg-card flex items-center gap-3 rounded-lg border p-3 ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      {sortable && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground cursor-grab touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-5" />
        </button>
      )}
      <CategoryAvatar category={category} />
      <span className="text-foreground font-medium">{category.name}</span>
    </div>
  );
};

type SectionProps = {
  group: FullCategoryGroupFragment;
  categories: FullCategoryFragment[];
  memberIds: string[];
  /** Null when the members are already in alphabetical order. */
  onAlphabetize: (() => void) | null;
  onRename: (name: string) => void;
  onSetColor: (color: string | null) => void;
  onDelete: () => void;
};

/** A group section, itself sortable by its header grip. */
const SortableGroupSection = ({
  group,
  categories,
  memberIds,
  onAlphabetize,
  onRename,
  onSetColor,
  onDelete,
}: SectionProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({id: `${GROUP_PREFIX}${group._id}`});
  const [renaming, setRenaming] = React.useState(false);
  const [coloring, setColoring] = React.useState(false);
  const [draftName, setDraftName] = React.useState(group.name);
  const groupColor = validCategoryColorOrNull(group.color ?? null);

  const startRename = () => {
    setDraftName(group.name);
    setRenaming(true);
  };

  const commitRename = () => {
    setRenaming(false);
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== group.name) {
      onRename(trimmed);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={{transform: CSS.Transform.toString(transform), transition}}
      className={isDragging ? 'opacity-50' : ''}
    >
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground cursor-grab touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        {renaming ? (
          <div className="flex items-center gap-1">
            <TextInput
              autoFocus
              value={draftName}
              onChange={setDraftName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === 'Escape') {
                  setRenaming(false);
                }
              }}
            />
            <Button variant="ghost" size="icon" onClick={commitRename}>
              <Check className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setRenaming(false)}
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-foreground text-sm font-semibold">
              {group.name}
            </h3>
            <span className="text-muted-foreground text-xs">
              {memberIds.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              title="Sort categories alphabetically"
              disabled={onAlphabetize == null}
              onClick={() => onAlphabetize?.()}
            >
              <ArrowDownAZ className="size-3.5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={startRename}>
              <Pencil className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Group color"
              onClick={() => setColoring((v) => !v)}
            >
              {groupColor != null ? (
                <span
                  className={cn(
                    'size-3.5 rounded-full',
                    categorySwatchClasses[groupColor],
                  )}
                />
              ) : (
                <Palette className="size-3.5" />
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete}>
              <Trash2 className="size-3.5" />
            </Button>
          </>
        )}
      </div>
      {coloring && !renaming && (
        <div className="mb-2 flex items-center gap-1.5 pl-6">
          <button
            type="button"
            title="Automatic color"
            onClick={() => {
              onSetColor(null);
              setColoring(false);
            }}
            className={cn(
              'border-border text-muted-foreground flex size-6 cursor-pointer items-center justify-center rounded-full border',
              groupColor == null && 'ring-primary ring-2',
            )}
          >
            <Ban className="size-3.5" />
          </button>
          {categoryColors.map((colorName) => (
            <button
              key={colorName}
              type="button"
              title={colorName}
              onClick={() => {
                onSetColor(colorName);
                setColoring(false);
              }}
              className={cn(
                'size-6 cursor-pointer rounded-full',
                categorySwatchClasses[colorName],
                groupColor === colorName && 'ring-primary ring-2',
              )}
            />
          ))}
        </div>
      )}
      <SortableContext items={memberIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {categories.map((category) => (
            <RearrangeCategoryRow
              key={category._id}
              category={category}
              sortable
            />
          ))}
          {categories.length === 0 && (
            <div className="border-border text-muted-foreground rounded-lg border border-dashed p-3 text-sm">
              No categories — assign one from its category page
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

type Props = {
  categories: FullCategoryFragment[];
  groups: FullCategoryGroupFragment[];
  /**
   * False until the AllCategoryGroups query has a response. Creation is
   * blocked until then: a group created against an unloaded cache can be
   * erased when the in-flight response (computed before the create) lands
   * afterward and replaces the list without it.
   */
  groupsLoaded: boolean;
};

/**
 * Rearrange mode for the categories page: drag group headers to reorder
 * groups, drag rows to reorder categories within their group. Categories
 * don't move between groups by dragging — that happens via the Group field
 * on the category's page. Each drop persists immediately
 * (reorderCategoryGroups). Group create/rename/delete are inline.
 */
export const RearrangeCategoriesView = ({
  categories,
  groups,
  groupsLoaded,
}: Props) => {
  const [draft, setDraftState] = React.useState<Draft>(() =>
    buildDraft(groups),
  );
  const draftRef = React.useRef(draft);
  const setDraft = React.useCallback((next: Draft) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  // Reconcile when groups are created/deleted or members change elsewhere
  React.useEffect(() => {
    setDraft(buildDraft(groups, draftRef.current));
  }, [groups, setDraft]);

  const [newGroupName, setNewGroupName] = React.useState('');
  // True from enqueue until the create task settles (see handleCreateGroup).
  // The ref mirrors the state synchronously — state updates flush on a
  // microtask, so back-to-back submissions in one tick would both read the
  // stale render value without it.
  const [createPending, setCreatePending] = React.useState(false);
  const createPendingRef = React.useRef(false);
  const [createGroup] = useCreateCategoryGroupMutation();
  const [renameGroup] = useUpdateCategoryGroupMutation();
  const [deleteGroup] = useDeleteCategoryGroupMutation();
  const [reorderGroups] = useReorderCategoryGroupsMutation();

  const categoryById = React.useMemo(
    () => new Map(categories.map((c) => [c._id, c])),
    [categories],
  );
  const groupById = new Map(groups.map((g) => [g._id, g]));

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
  );

  // Every resolver on the server reads Settings and rewrites categoryGroups
  // wholesale, so ANY two concurrent group mutations (reorder, create,
  // rename, delete) can clobber each other's writes. All of them run
  // through this one queue; failures surface in a banner instead of
  // silently pretending the save happened.
  // Failures are tracked per operation kind, and a success clears only its
  // own kind: a queued reorder succeeding must not erase the explanation
  // that an earlier rename failed, and a later group edit succeeding must
  // not hide that a drag was rolled back. Each banner stands until the
  // operation that produced it succeeds on a retry.
  const [saveErrors, setSaveErrors] = React.useState<
    Partial<Record<MutationKind, string>>
  >({});
  const queueRef = React.useRef<Promise<void>>(Promise.resolve());
  const enqueue = React.useCallback(
    (kind: MutationKind, task: () => Promise<unknown>) => {
      queueRef.current = queueRef.current.then(async () => {
        try {
          await task();
          setSaveErrors((prev) => {
            if (!(kind in prev)) {
              return prev;
            }
            const next = {...prev};
            delete next[kind];
            return next;
          });
        } catch (e: unknown) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const err = e as Error;
          setSaveErrors((prev) => ({...prev, [kind]: err.message}));
        }
      });
    },
    [],
  );

  // Latest props for queued tasks, which run after renders.
  const groupsRef = React.useRef(groups);
  groupsRef.current = groups;

  // At most one reorder task waits in the queue; it reads the latest draft
  // when it runs, so drops made while it waits coalesce into that send.
  const reorderQueuedRef = React.useRef(false);

  const persist = () => {
    if (reorderQueuedRef.current) {
      return;
    }
    reorderQueuedRef.current = true;
    enqueue('reorder', async () => {
      reorderQueuedRef.current = false;
      const snapshot = draftRef.current;
      try {
        await reorderGroups({
          variables: {
            groups: snapshot.order.map((id) => ({
              id,
              categoryIds: snapshot.members[id] ?? [],
            })),
          },
          update: (cache, result) => {
            const updated = result.data?.reorderCategoryGroups;
            if (updated) {
              setCategoryGroups(cache, updated);
            }
          },
        });
      } catch (e) {
        // The optimistic draft didn't save. If another reorder is already
        // queued it will resend the user's latest draft and supersede this
        // failure — resetting here would make that task snapshot the old
        // order and silently discard the newest drag. Only with nothing
        // queued do we fall back to the last server-confirmed state, so
        // the screen doesn't keep an order that doesn't exist.
        if (!reorderQueuedRef.current) {
          setDraft(buildDraft(groupsRef.current));
        }
        throw e;
      }
    });
  };

  const handleCreateGroup = () => {
    const name = newGroupName.trim();
    if (!name || createPendingRef.current || !groupsLoaded) {
      return;
    }
    // Apollo's loading flag only covers the mutation itself; while the task
    // waits in the queue it is still false, so without this flag repeated
    // clicks/Enters would enqueue the same name once per press (names are
    // not unique server-side — each would create a separate group).
    createPendingRef.current = true;
    setCreatePending(true);
    enqueue('create', async () => {
      try {
        const result = await createGroup({
          variables: {name},
          update: (cache, mutationResult) => {
            const created = mutationResult.data?.createCategoryGroup;
            if (created) {
              addCategoryGroup(cache, created);
            }
          },
        });
        // Reconcile the draft synchronously: the cache write above only
        // schedules the props->draft effect, so a reorder queued right
        // behind this task would otherwise snapshot a draft that predates
        // the structural change and be rejected for a group-set mismatch.
        const created = result.data?.createCategoryGroup;
        if (created && !draftRef.current.order.includes(created._id)) {
          setDraft({
            order: [...draftRef.current.order, created._id],
            members: {...draftRef.current.members, [created._id]: []},
          });
        }
        setNewGroupName('');
      } finally {
        createPendingRef.current = false;
        setCreatePending(false);
      }
    });
  };

  const handleRename = (groupId: string, name: string) => {
    // Renames don't change the order/membership structure, so no draft
    // reconciliation is needed here.
    enqueue('rename', () => renameGroup({variables: {id: groupId, name}}));
  };

  const handleSetColor = (groupId: string, color: string | null) => {
    // Color-only update: no name is sent, so a rename queued just before
    // this can never be reverted by a stale captured name.
    enqueue('rename', () =>
      renameGroup({variables: {id: groupId, color}}),
    );
  };

  const handleDelete = (groupId: string) => {
    enqueue('delete', async () => {
      const result = await deleteGroup({
        variables: {id: groupId},
        update: (cache, mutationResult) => {
          const updated = mutationResult.data?.deleteCategoryGroup;
          if (updated) {
            setCategoryGroups(cache, updated);
          }
        },
      });
      // Same synchronous reconciliation as creation, for the same reason.
      const updated = result.data?.deleteCategoryGroup;
      if (updated) {
        setDraft(buildDraft(updated, draftRef.current));
      }
    });
  };

  /** Member IDs sorted by category name; unknown IDs keep their order last. */
  const alphabetized = React.useCallback(
    (memberIds: string[]) =>
      [...memberIds].sort((a, b) =>
        (categoryById.get(a)?.name ?? '').localeCompare(
          categoryById.get(b)?.name ?? '',
        ),
      ),
    [categoryById],
  );

  const handleAlphabetize = (groupId: string) => {
    const current = draftRef.current;
    const ids = current.members[groupId] ?? [];
    setDraft({
      ...current,
      members: {...current.members, [groupId]: alphabetized(ids)},
    });
    persist();
  };

  /** The group ID containing a category row id, if any. */
  const containerOf = (id: string): string | null =>
    draftRef.current.order.find((groupId) =>
      (draftRef.current.members[groupId] ?? []).includes(id),
    ) ?? null;

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over) {
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    const current = draftRef.current;

    if (activeId.startsWith(GROUP_PREFIX)) {
      // Reorder whole groups by header drag; drops often land on a row —
      // resolve rows to their containing group.
      const activeKey = activeId.slice(GROUP_PREFIX.length);
      const overKey = overId.startsWith(GROUP_PREFIX)
        ? overId.slice(GROUP_PREFIX.length)
        : containerOf(overId);
      if (overKey != null && overKey !== activeKey) {
        const fromIndex = current.order.indexOf(activeKey);
        const toIndex = current.order.indexOf(overKey);
        if (fromIndex >= 0 && toIndex >= 0) {
          setDraft({
            ...current,
            order: arrayMove(current.order, fromIndex, toIndex),
          });
          persist();
        }
      }
      return;
    }

    // Category rows only reorder within their own group
    const container = containerOf(activeId);
    if (container == null || containerOf(overId) !== container) {
      return;
    }
    const ids = current.members[container] ?? [];
    const fromIndex = ids.indexOf(activeId);
    const toIndex = ids.indexOf(overId);
    if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
      setDraft({
        ...current,
        members: {
          ...current.members,
          [container]: arrayMove(ids, fromIndex, toIndex),
        },
      });
      persist();
    }
  };

  const groupedIds = new Set(
    draft.order.flatMap((id) => draft.members[id] ?? []),
  );
  const ungrouped = categories.filter((c) => !groupedIds.has(c._id));

  return (
    <div className="flex flex-col gap-6">
      {MUTATION_KINDS.flatMap((kind) => {
        const message = saveErrors[kind];
        return message == null
          ? []
          : [
              <div
                key={kind}
                className="bg-destructive/10 text-destructive rounded-md p-3 text-sm"
              >
                {MUTATION_KIND_LABELS[kind]}: {message}
              </div>,
            ];
      })}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={draft.order.map((id) => `${GROUP_PREFIX}${id}`)}
          strategy={verticalListSortingStrategy}
        >
          {draft.order.flatMap((groupId) => {
            const group = groupById.get(groupId);
            if (!group) {
              return [];
            }
            const memberIds = draft.members[groupId] ?? [];
            const members = memberIds.flatMap((id) => {
              const category = categoryById.get(id);
              return category ? [category] : [];
            });
            const sorted = alphabetized(memberIds);
            const alreadySorted = sorted.every((id, i) => id === memberIds[i]);
            return [
              <SortableGroupSection
                key={groupId}
                group={group}
                categories={members}
                memberIds={memberIds}
                onAlphabetize={
                  alreadySorted ? null : () => handleAlphabetize(groupId)
                }
                onRename={(name) => handleRename(groupId, name)}
                onSetColor={(color) => handleSetColor(groupId, color)}
                onDelete={() => handleDelete(groupId)}
              />,
            ];
          })}
        </SortableContext>
      </DndContext>

      <div className="flex items-center gap-2">
        <TextInput
          value={newGroupName}
          onChange={setNewGroupName}
          placeholder="New group name"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCreateGroup();
            }
          }}
        />
        <Button
          variant="outline"
          className="shrink-0 whitespace-nowrap"
          onClick={handleCreateGroup}
          disabled={createPending || !groupsLoaded || !newGroupName.trim()}
        >
          <Plus className="size-4" />
          Add group
        </Button>
      </div>

      {ungrouped.length > 0 && (
        <div>
          <div className="mb-2 flex items-baseline gap-2">
            <h3 className="text-foreground text-sm font-semibold">Ungrouped</h3>
            <span className="text-muted-foreground text-xs">
              sorted by name — set a group from each category's page
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {ungrouped.map((category) => (
              <RearrangeCategoryRow
                key={category._id}
                category={category}
                sortable={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
